import clsx from 'clsx';

import { tokens } from '../../../theme/generated/tokens';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';
import { Panel, PanelProps } from '../Panel';

const classNamesFactory = classNamesFor('ChoicePanel');

export const choicePanelClassNames = {
  root: classNamesFactory.root(),
  selected: classNamesFactory.attribute('selected'),
  disabled: classNamesFactory.attribute('disabled'),
};

export type ChoicePanelProps = Omit<PanelProps, 'onClick'> & {
  /** Whether the panel is selected */
  selected?: boolean;
  /** Whether the panel is disabled */
  disabled?: boolean;
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
};

/**
 * An interactive panel that can be selected.
 * Used for presenting choices — extends Panel with hover, selected, and disabled states.
 */
export const ChoicePanel = ({
  children,
  className,
  selected = false,
  disabled = false,
  onClick,
  testId,
  sx,
  ...rest
}: ChoicePanelProps) => {
  return (
    <Panel
      className={clsx(
        choicePanelClassNames.root,
        selected && choicePanelClassNames.selected,
        disabled && choicePanelClassNames.disabled,
        className
      )}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-pressed={selected}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      testId={testId}
      sx={mergeSx(
        {
          backgroundColor: (() => {
            if (disabled && selected) {
              return tokens.theme.disabled.surface;
            }
            if (selected) {
              return tokens.theme.bg.selection.primarySubtle;
            }
            return tokens.theme.bg.structural.surface;
          })(),
          borderColor: (() => {
            if (disabled) {
              return tokens.theme.disabled.border;
            }
            if (selected) {
              return tokens.theme.border.selection.primary;
            }
            return tokens.theme.border.structural.neutral;
          })(),
          cursor: disabled || selected ? 'default' : 'pointer',
          pointerEvents: disabled ? 'none' : undefined,
          transition: 'background-color 150ms, border-color 150ms',
          ...(!disabled &&
            !selected && {
              '&:hover': {
                borderColor: tokens.theme.border.action.neutralHover,
              },
            }),
        },
        sx
      )}
      {...rest}
    >
      {children}
    </Panel>
  );
};
